import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from './AuthContext';
import api from '../services/api';

// ---------------------------------------------------------------------------
// How much support is waiting for you.
//
// The ticket system had no notion of unread at all: an admin only discovered a
// ticket by opening the support screen and reading down the list, and a seller
// only discovered they had been answered by going to look. Both are the kind
// of thing you find out about days late.
//
// One small poll, shared by every screen, so the number in the header is the
// same number the support page shows. Derived server-side from the ticket
// timestamps — nothing here counts anything itself.
// ---------------------------------------------------------------------------

const POLL_MS = 60000;

export const SupportUnreadContext = createContext({ unread: 0, open: 0, refresh: () => {} });

export function SupportUnreadProvider({ children }) {
  const { isAuthenticated } = useContext(AuthContext);
  const [state, setState] = useState({ unread: 0, unreadOpen: 0, open: 0 });

  const refresh = useCallback(async () => {
    if (!isAuthenticated) { setState({ unread: 0, unreadOpen: 0, open: 0 }); return; }
    try {
      const res = await api.get('/support/unread');
      setState(res.data);
    } catch {
      // A failed count must never break the page it sits on. The badge simply
      // does not update.
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
    if (!isAuthenticated) return undefined;

    // Paused while the tab is hidden — polling a background tab every minute
    // for a number nobody is looking at is just noise in the log.
    const tick = () => { if (!document.hidden) refresh(); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, [isAuthenticated, refresh]);

  return (
    <SupportUnreadContext.Provider value={{ ...state, refresh }}>
      {children}
    </SupportUnreadContext.Provider>
  );
}

export const useSupportUnread = () => useContext(SupportUnreadContext);
